import React, { FunctionComponent, useState, MouseEvent } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { TableRow, TableCell } from "@material-ui/core";
import moment from "moment-timezone";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { SystemEmailTemplate, EMAIL_TEMPLATE_CATEGORIES } from "model/SystemEmailTemplate";
import { togglePublishThunk } from "actions/systemEmailTemplates/SystemEmailTemplatesActions";
import { Button, ConfirmDialog } from "components/UI";

interface Props {
  template: SystemEmailTemplate;
  onSelect: (template: SystemEmailTemplate) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#FAFAFA !important"
      },
      "&.MuiTableRow-root": {
        verticalAlign: "baseline"
      }
    },
    cell: {
      [theme.breakpoints.down("sm")]: {
        fontSize: "9px",
        paddingTop: "8px", paddingBottom: "8px",
        paddingLeft: "28px", paddingRight: "28px"
      },
      [theme.breakpoints.only("md")]: {
        fontSize: "11px",
        paddingTop: "13px", paddingBottom: "13px",
        paddingLeft: "45px", paddingRight: "45px"
      },
      [theme.breakpoints.only("lg")]: {
        fontSize: "14px",
        paddingTop: "17px", paddingBottom: "17px",
        paddingLeft: "60px", paddingRight: "60px"
      },
      [theme.breakpoints.up("xl")]: {
        fontSize: "18px",
        paddingTop: "25px", paddingBottom: "25px",
        paddingLeft: "90px", paddingRight: "90px"
      }
    },
    badge: {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "999px",
      fontFamily: "Poppins",
      fontWeight: 500,
      lineHeight: 1.2,
      [theme.breakpoints.down("sm")]: { fontSize: "9px" },
      [theme.breakpoints.only("md")]: { fontSize: "10px" },
      [theme.breakpoints.only("lg")]: { fontSize: "12px" },
      [theme.breakpoints.up("xl")]:  { fontSize: "14px" }
    },
    badgePublished: {
      backgroundColor: "#E4F3EA",
      color: "#2E7D5B"
    },
    badgeDraft: {
      backgroundColor: "#EEEEEE",
      color: "#666666"
    }
  })
);

/**
 * Lookup helper — falls back to the raw category id if it isn't in the
 * canonical list (shouldn't happen, but protects against legacy data).
 */
const friendlyCategory = (id: SystemEmailTemplate["category"]): string => {
  const option = EMAIL_TEMPLATE_CATEGORIES.find(c => c.id === id);
  return option ? option.name : id;
};

/**
 * Row clicks open the Preview modal. The Publish/Unpublish action lives in
 * the trailing action cell so super admins can toggle status without paying
 * the preview fetch round-trip — clicks there `stopPropagation` so the row's
 * onSelect doesn't fire.
 *
 * Unpublish shows a confirm dialog (marketplaces will stop seeing the
 * template in their picker). Publish is a one-click action — reversible and
 * low-risk.
 */
export const TemplateRow: FunctionComponent<Props> = ({ template, onSelect }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [toggling, setToggling] = useState<boolean>(false);
  const [confirmUnpublishOpen, setConfirmUnpublishOpen] = useState<boolean>(false);

  const runToggle = async (nextPublished: boolean) => {
    setToggling(true);
    await (dispatch(togglePublishThunk(template.id, nextPublished)) as any);
    setToggling(false);
  };

  const handleActionClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (template.published) {
      setConfirmUnpublishOpen(true);
    } else {
      runToggle(true);
    }
  };

  const handleConfirmUnpublish = () => {
    setConfirmUnpublishOpen(false);
    runToggle(false);
  };

  return (
    <>
      <TableRow
        className={classes.row}
        onClick={() => onSelect(template)}
      >
        <TableCell className={classes.cell}>{template.name}</TableCell>
        <TableCell className={classes.cell}>{friendlyCategory(template.category)}</TableCell>
        <TableCell className={classes.cell}>
          <span
            className={clsx(
              classes.badge,
              template.published ? classes.badgePublished : classes.badgeDraft
            )}
          >
            {template.published ? "Published" : "Draft"}
          </span>
        </TableCell>
        <TableCell className={classes.cell}>
          {moment(template.updatedAt).format("MMM D, YYYY")}
        </TableCell>
        <TableCell className={classes.cell} onClick={event => event.stopPropagation()}>
          <Button
            label={template.published ? "Unpublish" : "Publish"}
            variant="secondary"
            size="small"
            onClick={handleActionClick}
            loading={toggling}
            disabled={toggling}
          />
        </TableCell>
      </TableRow>

      <ConfirmDialog
        open={confirmUnpublishOpen}
        question={
          <div style={{ textAlign: "center" }}>
            {`Unpublish "${template.name}"? Businesses will no longer see it in their template picker.`}
          </div>
        }
        cancelButtonLabel="Keep published"
        confirmButtonLabel="Unpublish"
        onCancel={() => setConfirmUnpublishOpen(false)}
        onConfirm={handleConfirmUnpublish}
      />
    </>
  );
};

export default TemplateRow;
